import { Form, FormHandlers, FormProps } from '@doveit/bricks';
import React from 'react';
import { INPUT_PLACEHOLDERS } from '../../../constants';
import { deepMerge } from '../../../utils/object/object';
import editAgentRegistryValidationSchema from './EditAgentRegistryForm.schema';
import { GeoFormModel } from '../../../geo/components/form-geo-field/types';
import { GEO_FORM_MODEL_DEFAULT_VALUES } from '../../../geo/components/form-geo-field/constants';
import FormGeoField from '../../../geo/components/form-geo-field/FormGeoField';

export interface EditAgentRegistryFormModel {
  workName: string,
  personalName: string,
  workSurname: string,
  personalSurname: string,
  workEmail: string,
  personalEmail: string,
  pecEmail: string,
  workMobile: string,
  personalMobile: string,
  fiscalCode: string,
  residenceAddress: GeoFormModel,
  shippingAddress: GeoFormModel,
}

export interface EditAgentRegistryFormProps {
  initialValues?: Partial<EditAgentRegistryFormModel>,
  loading?: FormProps<EditAgentRegistryFormModel>['loading'],
  disabled?: FormProps<EditAgentRegistryFormModel>['disabled'],
  innerRef?: React.MutableRefObject<FormHandlers>,
  onSubmit: FormProps<EditAgentRegistryFormModel>['onSubmit'],
}

export const EDIT_AGENT_REGISTRY_FORM_DEFAULT_VALUES: EditAgentRegistryFormModel = {
  workName: '',
  personalName: '',
  workSurname: '',
  personalSurname: '',
  workEmail: '',
  personalEmail: '',
  pecEmail: '',
  workMobile: '',
  personalMobile: '',
  fiscalCode: '',
  residenceAddress: GEO_FORM_MODEL_DEFAULT_VALUES,
  shippingAddress: GEO_FORM_MODEL_DEFAULT_VALUES,
};

const EditAgentRegistryForm: React.FC<EditAgentRegistryFormProps> = (props) => {
  const { initialValues = {}, innerRef, ...rest } = props;

  const toggleManualEntryForResidenceAddress = React.useMemo(
    () => !initialValues.residenceAddress?.latitude
      && !initialValues.residenceAddress?.longitude
      && !!initialValues.residenceAddress?.normalizedAddress?.description,
    [initialValues.residenceAddress?.latitude, initialValues.residenceAddress?.longitude, initialValues.residenceAddress?.normalizedAddress],
  );

  const toggleManualEntryForShippingAddress = React.useMemo(
    () => !initialValues.shippingAddress?.latitude
      && !initialValues.shippingAddress?.longitude
      && !!initialValues.shippingAddress?.normalizedAddress?.description,
    [initialValues.shippingAddress?.latitude, initialValues.shippingAddress?.longitude, initialValues.shippingAddress?.normalizedAddress?.description],
  );

  const mergedValues: EditAgentRegistryFormModel = React.useMemo(() => deepMerge(
    EDIT_AGENT_REGISTRY_FORM_DEFAULT_VALUES,
    initialValues,
  ), [initialValues]);

  return (
    <Form
      initialValues={mergedValues}
      validationSchema={editAgentRegistryValidationSchema}
      enableReinitialize
      innerRef={innerRef}
      {...rest}
    >
      <Form.Fieldset legend="Nome e cognome">
        <Form.Item size={{ MD: 1 / 2 }}>
          <Form.Input
            name="workName"
            label="Nome"
            aria-label="Campo per inserire il nome lavorativo dell'agente"
            placeholder={INPUT_PLACEHOLDERS.NAME}
            required
          />
        </Form.Item>

        <Form.Item size={{ MD: 1 / 2 }}>
          <Form.Input
            name="workSurname"
            label="Cognome"
            aria-label="Campo per inserire il cognome lavorativo dell'agente"
            placeholder={INPUT_PLACEHOLDERS.SURNAME}
            required
          />
        </Form.Item>

        <Form.Item size={{ MD: 1 / 2 }}>
          <Form.Input
            name="personalName"
            label="Nome reale"
            aria-label="Campo per inserire il nome reale dell'agente"
            placeholder={INPUT_PLACEHOLDERS.NAME}
          />
        </Form.Item>

        <Form.Item size={{ MD: 1 / 2 }}>
          <Form.Input
            name="personalSurname"
            label="Cognome reale"
            aria-label="Campo per inserire il cognome reale dell'agente"
            placeholder={INPUT_PLACEHOLDERS.SURNAME}
          />
        </Form.Item>
      </Form.Fieldset>

      <Form.Fieldset legend="Telefono">
        <Form.Item size={{ MD: 1 / 2 }}>
          <Form.Input
            name="workMobile"
            label="Lavorativo"
            aria-label="Campo per inserire il telefono lavorativo dell'agente"
            placeholder={INPUT_PLACEHOLDERS.CONTACT_PHONE_NUMBER}
          />
        </Form.Item>

        <Form.Item size={{ MD: 1 / 2 }}>
          <Form.Input
            name="personalMobile"
            label="Personale"
            aria-label="Campo per inserire il telefono personale dell'agente"
            placeholder={INPUT_PLACEHOLDERS.CONTACT_PHONE_NUMBER}
            required
          />
        </Form.Item>
      </Form.Fieldset>

      <Form.Fieldset legend="Email">
        <Form.Item size={{ MD: 1 / 2 }}>
          <Form.Input
            name="workEmail"
            label="Lavorativa"
            aria-label="Campo per inserire l'email lavorativa dell'agente"
            placeholder={INPUT_PLACEHOLDERS.DOVE_IT_EMAIL}
          />
        </Form.Item>

        <Form.Item size={{ MD: 1 / 2 }}>
          <Form.Input
            name="personalEmail"
            label="Personale"
            aria-label="Campo per inserire l'email personale dell'agente"
            placeholder={INPUT_PLACEHOLDERS.CONTACT_EMAIL}
            required
          />
        </Form.Item>

        <Form.Item size={{ MD: 1 / 2 }}>
          <Form.Input
            name="pecEmail"
            label="PEC"
            aria-label="Campo per inserire la PEC dell'agente"
            placeholder={INPUT_PLACEHOLDERS.PEC_EMAIL}
          />
        </Form.Item>
      </Form.Fieldset>

      <Form.Fieldset legend="Indirizzi">
        <Form.Item>
          <FormGeoField
            aria-label="Indirizzo di residenza"
            name="residenceAddress"
            enableManualEntry
            toggleManualEntry={toggleManualEntryForResidenceAddress}
            labels={{
              normalizedAddress: 'Residenza',
            }}
          />
        </Form.Item>

        <Form.Item>
          <FormGeoField
            aria-label="Indirizzo di spedizione materiali"
            name="shippingAddress"
            enableManualEntry
            toggleManualEntry={toggleManualEntryForShippingAddress}
            labels={{
              normalizedAddress: 'Invio materiali',
            }}
          />
        </Form.Item>
      </Form.Fieldset>

      <Form.Fieldset legend="Codice fiscale">
        <Form.Item size={{ MD: 2 / 3 }}>
          <Form.Input
            name="fiscalCode"
            aria-label="Campo per inserire il codice fiscale dell'agente"
            placeholder={INPUT_PLACEHOLDERS.CONTACT_FISCALCODE}
          />
        </Form.Item>
      </Form.Fieldset>
    </Form>
  );
};

export default EditAgentRegistryForm;
